import type { Page } from '../../types';
import { COUNTRIES } from '../../defaultContent';

const addPreventedError: Page[] = [
  {
    pageType: 'question',
    title: 'Future loss prevented (error)',
    name: 'prevented-error-declare-page',
    description:
      'The Prevented categories are aligned to definitions referenced in the Fraud Measurement Standard:' +
      '<li>Future Loss Prevented: These are calculated savings that evidence that fraud/error would have continued for a period of time had it not been detected in the first place, and that subsequently would have resulted in a financial loss. These calculated savings should be arrived at through evidenced and agreed assumptions.</li></ul><br/>' +
      'The reporting in this section refers to instances where the prevention of fraud (and in some cases, error) can be evidenced with certainty or via an assured prevention methodology. Reported prevention is a result of active detection or evidenced via a measurement exercise. Values recorded here should be evidenced as either an evaluated or calculated saving, and should therefore reflect a realistic and legitimate saving/benefit to the exchequer. Only methodologies approved by the Prevention Panel can be used to calculate a saving.<br><br>' +
      'The Prevention Panel is facilitated by the PSFA for use by all public bodies wishing to claim prevented savings based on an estimated value. A methodology should either be to calculate a Future Loss Prevented or Upstream Prevention estimate. The purpose is to ensure prevented estimates and the methodologies used to calculate savings are reasonable, robust and credible.<br><br>' +
      'The Prevention Panel Methodology Bank provides a repository of all approved savings methodologies. In order for a new methodology to be approved by the Prevention Panel a Prevention Panel submission must be completed by the originating department/organisation. The submission must be sent to psfa.performance@cabinetoffice.gov.uk no later than three weeks prior to the next Prevention Panel and on the correct template. If you require further information about the process, the Methodology Bank or upcoming meeting dates please email psfa.performance@cabinetoffice.gov.uk.<br><br>' +
      'Please Note: All entries in this section must be allocated a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail.<br><br>',
    questions: [
      {
        questionType: 'radio',
        title: 'Do you have any instances of Future loss prevented (error) to declare?',
        name: 'prevented_future_error_declare',
        options: ['Yes', 'No'],
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'list',
    title: 'Add an instance of future prevented error',
    name: 'prevented-error-add-instance',
    type: 'nilreturn',
    fastToggle: true,
    total: {
      columns: [
        { title: 'Number of instances', field: 'prevented_future_error_impact' },
        { title: 'Total Value', field: 'prevented_future_error' },
      ],
    },
    description:
      'Prevented values should be evidenced with certainty. Prevented actuals should only refer to instances of fraud prevented, not error. Entries should also include selections from the Prevented Actuals typology dropdowns:' +
      '<ul><li>the business area where the fraud was prevented</li><li>the nature of deception - whether the prevented fraud involved a false representation, a failure to declare or disclose, or an abuse of position</li><li>the detailed nature of deception - a dependent dropdown selection on the specifics of the fraudulent activity</li><li>if applicable, the fraud enabler(s)</li><li>whether or not the fraud related to a COVID-19 scheme or activity</li></ul><br/>' +
      'Future loss prevented values should be determined through evidenced and agreed assumptions that a loss in the future was prevented as a result of detecting the fraud (e.g. payments would have continued). Savings should not be projected beyond a 12 month period. The savings methodology used should either be assured by the Prevention Panel, or refer to an approved methodology within the Prevention Panel Methodology Bank.<br><br>',
    disabled: [
      {
        field: 'prevented_future_error_declare',
        condition: 'equals', // optional
        value: 'No',
      },
    ],
    questions: [
      {
        questionType: 'radio',
        title: 'Who was responsible for the error?',
        name: 'prevented_future_error_responsible',
        detailsHeading: 'Who was responsible for the error?',
        hint: 'who was responsible for the unintentional mistake (error)?',
        detailsContent:
          '<b>Customer:</b> a member of the public or someone acting on behalf of a business or charity' +
          '<br/><b>Official:</b> a government department, public body or service' +
          '<br/><b>Supplier:</b> a person or business contracted to supply a service' +
          '<br/><b>Unknown:</b> I don&apos;t know who was responsible for the mistake?',
        options: ['Customer', 'Official', 'Supplier', 'Unknown'],
        rules: { required: true },
      },
      {
        questionType: 'select',
        title: 'Which spending area was the error prevented in?',
        name: 'prevented_future_error_area',
        detailsHeading: 'Help with spending areas',
        detailsContent:
          ' <b>Benefits:</b> money given by the government to support members of the public' +
          ' <br/><b>Charges & other non-tax income:</b> money owed to public sector organisations (for example, court fines or prescription fees)' +
          ' <br/><b>Grants:</b> money given by a public sector organisation for a specific purpose' +
          ' <br/><b>Licences and permits:</b> licences, permits or certificates that grant permissions (for example, a Blue Badge, Surveyor licence, marriage certificate)' +
          ' <br/><b>Loans & credit guarantees:</b> money loaned or guaranteed by a public sector organisation' +
          ' <br/><b>Personnel:</b> employees (including contractors and agency workers)' +
          ' <br/><b>Procurement:</b> goods or services bought by public sector organisations (including suppliers)' +
          ' <br/><b>Special payments:</b> money given in unique or specific circumstances (for example, compensation)' +
          ' <br/><b>Subsidies:</b> support or incentives given by the government' +
          ' <br/><b>Tax & duties:</b> mandatory government charge for goods, income or services',
        options: [
          'Benefits',
          'Charges & other non-tax income',
          'Grants',
          'Licences and permits',
          'Loans & credit guarantees',
          'Personnel',
          'Procurement',
          'Special payments',
          'Subsidies',
          'Tax & duties',
        ],
        hintOption: 'Please Select',
        rules: { required: true },
      },
      {
        questionType: 'instanceInput',
        title: 'How many instances were prevented?',
        name: 'prevented_future_error_impact',
        txt: `Total number of instances.`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'costInput',
        displaySeperator: 'thousand',
        title: 'What was the value of the prevented error?',
        name: 'prevented_future_error',
        txt: `Total value`,
        rules: {
          required: true,
          decimal: 'decimal(2)',
        },
      },
      {
        questionType: 'textInput',
        title: 'Enter the methodology number here',
        name: 'prevented_future_error_methodology_number',
        rules: {
          required: true,
        },
      },
      {
        questionType: 'textarea',
        title: 'Comments',
        name: 'prevented_future_error_comments',
        hint: `Please ensure you do not include any personal or sensitive information`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'radio',
        title: 'Counter fraud involvement?',
        name: 'prevented_future_error_involved',
        detailsHeading: 'Help with counter fraud involvement',
        detailsContent:
          '<b>Counter fraud delivered:</b> the counter fraud resource carried out the prevention activity' +
          '<br/><b>Counter fraud influenced:</b> the counter fraud resource influenced the prevention activity',
        options: ['Counter fraud delivered', 'Counter fraud influenced'],
        rules: { required: true },
      },
      {
        questionType: 'radio',
        title: 'Does this relate to BAU or C19 activity?',
        name: 'prevented_future_error_c19',
        options: ['Business as usual (BAU)', 'Covid-19 (C19)'],
        rules: { required: true },
      },
    ],
  },
];
export default addPreventedError;
